/**
 * WP-07B — Authenticated server entry points for the governed evidence authority.
 *
 * The actor is NEVER accepted from the caller. `requireSupabaseAuth` validates the
 * request bearer token against Auth and puts the verified `userId` on the context;
 * that value alone becomes the evidence actor.
 *
 * The privileged adapters are imported INSIDE each handler, so the service-role
 * client and the private bucket never enter a client-reachable module graph.
 *
 * Every response is the stable, non-sensitive outcome vocabulary: on failure a
 * reason code and a retry hint, never a provider message, SQL text, object path,
 * or raw exception.
 */

import { createServerFn } from "@tanstack/react-start";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  authorizeEvidenceDownload,
  bindEvidenceToCondition,
  deleteEvidence,
  evidenceMaySupportCondition,
  listLoopEvidence,
  processEvidence,
  recordEvidenceUpload,
  registerEvidence,
} from "./evidenceCommand";
import {
  decodeBase64,
  validateEvidenceConditionInput,
  validateEvidenceIdInput,
  validateEvidenceUploadInput,
  validateLoopIdInput,
  validateRegisterEvidenceInput,
} from "./evidenceRequest";

export const listLoopEvidenceRecords = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(validateLoopIdInput)
  .handler(async ({ data, context }) => {
    const { hostedEvidenceDeps } = await import("./evidenceDeps.server");
    return listLoopEvidence(hostedEvidenceDeps, { userId: context.userId }, data);
  });

export const registerEvidenceRecord = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(validateRegisterEvidenceInput)
  .handler(async ({ data, context }) => {
    const { hostedEvidenceDeps } = await import("./evidenceDeps.server");
    return registerEvidence(hostedEvidenceDeps, { userId: context.userId }, data);
  });

export const uploadEvidenceBytes = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(validateEvidenceUploadInput)
  .handler(async ({ data, context }) => {
    const { hostedEvidenceDeps } = await import("./evidenceDeps.server");
    return recordEvidenceUpload(
      hostedEvidenceDeps,
      { userId: context.userId },
      {
        evidenceId: data.evidenceId,
        bytes: decodeBase64(data.bytesBase64),
      },
    );
  });

export const inspectEvidenceRecord = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(validateEvidenceIdInput)
  .handler(async ({ data, context }) => {
    const { hostedEvidenceDeps } = await import("./evidenceDeps.server");
    return processEvidence(hostedEvidenceDeps, { userId: context.userId }, data);
  });

export const bindEvidenceCondition = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(validateEvidenceConditionInput)
  .handler(async ({ data, context }) => {
    const { hostedEvidenceDeps } = await import("./evidenceDeps.server");
    return bindEvidenceToCondition(hostedEvidenceDeps, { userId: context.userId }, data);
  });

export const requestEvidenceDownload = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(validateEvidenceIdInput)
  .handler(async ({ data, context }) => {
    const { hostedEvidenceDeps } = await import("./evidenceDeps.server");
    return authorizeEvidenceDownload(hostedEvidenceDeps, { userId: context.userId }, data);
  });

export const removeEvidenceRecord = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(validateEvidenceIdInput)
  .handler(async ({ data, context }) => {
    const { hostedEvidenceDeps } = await import("./evidenceDeps.server");
    return deleteEvidence(hostedEvidenceDeps, { userId: context.userId }, data);
  });

export const checkEvidenceSupportsCondition = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(validateEvidenceConditionInput)
  .handler(async ({ data, context }) => {
    const { hostedEvidenceDeps } = await import("./evidenceDeps.server");
    return evidenceMaySupportCondition(hostedEvidenceDeps, { userId: context.userId }, data);
  });
